import type { QueryClient } from '@tanstack/vue-query'

// The two lists of pending invitations — the organization's and each workspace's — are one set of
// rows seen from two screens, and a write on either screen leaves the other stale. Their keys live
// in this leaf so that the settings and the assets queries both refresh both without importing
// each other.

/** The organization's pending invitations, and the workspaces an invitation may seat somebody in. */
export const orgInvitationKeys = {
  all: ['org-invitations'] as const,
  workspaces: () => ['org-invitations', 'workspaces'] as const,
}

/**
 * What one workspace is still waiting on — the offers nobody has accepted yet. Seats and offers are
 * kept apart, as the two routes are: a seat is granted, an offer is outstanding.
 *
 * **Every write invalidates `all` and not one list**, because one address holds one offer across
 * the whole organization: a mint supersedes the address's previous invitation and carries its
 * other workspaces' seats onto the new one, and a cancel withdraws the whole invitation. Either way
 * a list of another workspace is left naming an invitation uid that is gone.
 */
export const workspaceInvitationKeys = {
  all: ['workspace-invitations'] as const,
  list: (workspaceId: string) => ['workspace-invitations', workspaceId] as const,
}

/**
 * Every pending list an invitation write leaves stale: the organization's, and each workspace's —
 * a mint supersedes the address's invitation in every list, and a cancel withdraws it from all of
 * them.
 */
export function invalidateInvitations(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: orgInvitationKeys.all }),
    queryClient.invalidateQueries({ queryKey: workspaceInvitationKeys.all }),
  ])
}
